import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './HoleEntryKeypad.css';

// `overlayZIndex`: this portals to document.body, so when it is opened from
// inside another modal the shared `.modal-overlay` class outranks its own 2000.
// The caller that stacks it says how high.
const HoleEntryKeypad = ({ holePars, holeStrokeIndices, onUpdate, onClose, allowDuplicateSI = false, overlayZIndex }) => {
    const [currentHole, setCurrentHole] = useState(() => {
        const idx = holePars.findIndex(p => !p);
        return idx >= 0 ? idx : 0;
    });
    const [field, setField] = useState('par');

    const currentPar = holePars[currentHole] || '';
    const currentSI = holeStrokeIndices[currentHole] || '';
    const filledCount = holePars.filter((p, i) => p && holeStrokeIndices[i]).length;

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const handleTap = (num) => {
        if (field === 'par') {
            onUpdate('hole_pars', currentHole, num);
            setField('si');
        } else {
            onUpdate('hole_stroke_indices', currentHole, num);
            if (currentHole < 17) {
                setCurrentHole(prev => prev + 1);
                setField('par');
            }
        }
    };

    return ReactDOM.createPortal(
        <div className="hole-keypad-overlay" style={overlayZIndex ? { zIndex: overlayZIndex } : undefined} onClick={onClose}>
            <div className="hole-keypad-card" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="hole-keypad-header">
                    <button className="hole-keypad-close" onClick={onClose}>&times;</button>
                </div>
                <div className="hole-keypad-hole-nav">
                    <button
                        className="hole-keypad-arrow"
                        onClick={() => { setCurrentHole(prev => prev - 1); setField('par'); }}
                        disabled={currentHole === 0}
                    >‹</button>
                    <div>
                        <h3>Hole {currentHole + 1}</h3>
                        <p>{filledCount}/18 complete</p>
                    </div>
                    <button
                        className="hole-keypad-arrow"
                        onClick={() => { setCurrentHole(prev => prev + 1); setField('par'); }}
                        disabled={currentHole === 17}
                    >›</button>
                </div>
                <div className="hole-keypad-divider"></div>

                {/* Par + SI display */}
                <div className="hole-keypad-values">
                    <div
                        className={`hole-keypad-value-box${field === 'par' ? ' hole-keypad-value-box--active' : ''}`}
                        onClick={() => setField('par')}
                    >
                        <span className="hole-keypad-value-label">Par</span>
                        <span className="hole-keypad-value-num">{currentPar || '–'}</span>
                    </div>
                    <div
                        className={`hole-keypad-value-box${field === 'si' ? ' hole-keypad-value-box--active' : ''}`}
                        onClick={() => {
                            if (field === 'si' && currentSI) {
                                onUpdate('hole_stroke_indices', currentHole, '');
                            } else {
                                setField('si');
                            }
                        }}
                    >
                        <span className="hole-keypad-value-label">SI {field === 'si' && currentSI ? '(tap to clear)' : ''}</span>
                        <span className="hole-keypad-value-num">{currentSI || '–'}</span>
                    </div>
                </div>

                {/* Keypad */}
                <div className="hole-keypad-grid">
                    {field === 'par' ? (
                        [3, 4, 5].map(n => (
                            <button
                                key={n}
                                className={`keypad-button${Number(currentPar) === n ? ' keypad-button--selected' : ''}`}
                                onClick={() => handleTap(n)}
                            >
                                {n}
                            </button>
                        ))
                    ) : (
                        Array.from({ length: 18 }, (_, i) => i + 1).map(n => {
                            const isSelected = Number(currentSI) === n;
                            const usedByIdx = !isSelected
                                ? holeStrokeIndices.findIndex((si, idx) => idx !== currentHole && Number(si) === n)
                                : -1;
                            const usedElsewhere = usedByIdx >= 0;
                            return (
                                <button
                                    key={n}
                                    className={`keypad-button${isSelected ? ' keypad-button--selected' : ''}${usedElsewhere ? ' keypad-button--used' : ''}`}
                                    onClick={() => {
                                        if (isSelected) {
                                            onUpdate('hole_stroke_indices', currentHole, '');
                                        } else if (usedElsewhere && !allowDuplicateSI) {
                                            // Legacy auto-swap: move the other hole's SI here.
                                            onUpdate('hole_stroke_indices', currentHole, n);
                                            onUpdate('hole_stroke_indices', usedByIdx, '');
                                            setCurrentHole(usedByIdx);
                                            setField('si');
                                        } else {
                                            // allowDuplicateSI: set the value as-is (a duplicate is
                                            // allowed and surfaced by the grid, never auto-swapped).
                                            handleTap(n);
                                        }
                                    }}
                                >
                                    {n}
                                </button>
                            );
                        })
                    )}
                </div>

                {/* Done */}
                <button className="hole-keypad-done-btn" onClick={onClose}>Done</button>
            </div>
        </div>,
        document.body
    );
};

export default HoleEntryKeypad;
